'use client';
import React from 'react';
import { Globe, Truck, PackageCheck, FileText, ArrowRight, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

/**
 * ExportCapabilitiesSection - Showcases the global supply chain and export readiness.
 * Designed for B2B procurement and international buyers.
 */
const ExportCapabilitiesSection = ({ light = false }) => {
    const capabilities = [
        {
            icon: Globe,
            title: "Global Supply Chain",
            desc: "Exporting high-quality labels to over 15+ countries across North America, Europe, and the Middle East."
        },
        {
            icon: Truck,
            title: "Logistics Support",
            desc: "Full support for FOB, CIF, and EXW terms with end-to-end freight coordination and tracking."
        },
        {
            icon: PackageCheck,
            title: "Export Packaging",
            desc: "Seaworthy, moisture-resistant packaging standards designed to preserve label integrity during long-haul transit."
        },
        {
            icon: FileText,
            title: "Documentation",
            desc: "Comprehensive export documentation support including Certificates of Analysis, Origin, and Compliance."
        }
    ];

    const stats = [
        { label: "Countries Served", value: "25+" },
        { label: "Annual Exports", value: "500+" },
        { label: "Incoterms", value: "FOB/CIF/EXW" },
        { label: "Lead Time", value: "14-21 Days" }
    ];

    return (
        <section className={`w-full section-shell ${light ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
            <div className="container-fluid">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

                    {/* Left side: Strategic Content */}
                    <div className="flex flex-col gap-10">
                        <div className="flex flex-col gap-6">
                            <div className="flex items-center gap-3">
                                <span className="w-10 h-px bg-blue-600"></span>
                                <span className="text-blue-600 text-sm font-bold tracking-[0.3em] uppercase">
                                    Export Excellence
                                </span>
                            </div>

                            <h2 className="text-3xl md:text-5xl font-bold text-[#0a1f33] leading-[1.15]">
                                Powering Global Brands with <br />
                                <span className="text-slate-500 font-serif italic">Export-Ready Labeling.</span>
                            </h2>

                            <p className="text-slate-600 text-lg leading-relaxed max-w-xl">
                                From our world-class HSIIDC Rai facility in India, we engineer and export high-quality labeling solutions for global Pharma and FMCG leaders. We are built for international scale, compliance, and reliability.
                            </p>
                        </div>

                        {/* Stats Row — flex-wrap, no min-w to prevent 320px overflow */}
                        <div className="flex flex-wrap gap-x-6 sm:gap-x-10 gap-y-5 py-8 border-y border-slate-200">
                            {stats.map((stat, idx) => (
                                <div key={idx} className="flex flex-col gap-1">
                                    <span className="text-xl sm:text-2xl font-bold text-[#0a1f33] whitespace-nowrap">{stat.value}</span>
                                    <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{stat.label}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-5">
                            <Link
                                href="/quote"
                                className="w-full sm:w-auto px-10 py-5 bg-[#0a1f33] text-white font-bold rounded-full hover:bg-blue-700 hover:-translate-y-1 transition-all duration-300 text-center shadow-lg"
                            >
                                Request Export Quote
                            </Link>
                            <Link
                                href="/portfolio"
                                className="w-full sm:w-auto px-10 py-5 border border-slate-200 text-[#0a1f33] font-bold rounded-full hover:bg-slate-50 hover:-translate-y-1 transition-all duration-300 text-center"
                            >
                                View Global Work
                            </Link>
                        </div>
                    </div>

                    {/* Right side: Capabilities Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {capabilities.map((item, idx) => (
                            <div key={idx} className="p-8 bg-white rounded-[32px] border border-slate-100 shadow-[0_10px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] transition-all duration-500 group">
                                <div className="p-4 rounded-2xl bg-blue-50 text-blue-600 w-fit mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                                    <item.icon className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-bold text-[#0a1f33] mb-3">{item.title}</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}

                        {/* Summary Card */}
                        <div className="sm:col-span-2 p-8 bg-blue-600 rounded-[32px] text-white flex flex-col sm:flex-row items-center justify-between gap-8">
                            <div className="flex flex-col gap-2">
                                <h4 className="text-xl font-bold">Ready for International Sampling?</h4>
                                <p className="text-blue-100 text-sm">We offer global sample shipping via FedEx/DHL for technical evaluation.</p>
                            </div>
                            <Link href="/contact" className="w-full sm:w-auto px-8 py-4 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-50 transition-all text-center shrink-0">
                                Request Samples
                            </Link>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ExportCapabilitiesSection;
